var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOkapiKy } from "@folio/stripes/core";
import { PublisherContactTypeEnum, } from "../types/Api";
import { createPublisherContactsSchema, } from "../utils/validationUtils";
import { fetchRegions } from "../api/regionService";
export var usePublisherContactsForm = function (initialValues) {
    var _a, _b;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var regionsData = useQuery(["regions"], function () {
        return fetchRegions(ky);
    }).data;
    var regionName = (_b = (_a = regionsData === null || regionsData === void 0 ? void 0 : regionsData.items) === null || _a === void 0 ? void 0 : _a.find(function (region) { return region.id === initialValues.regionId; })) === null || _b === void 0 ? void 0 : _b.name;
    var publisherContactsSchema = createPublisherContactsSchema(formatMessage);
    var localContacts = useMemo(function () { var _a; return ((_a = initialValues.contacts) !== null && _a !== void 0 ? _a : []).filter(function (contact) { return !contact.source; }); }, [initialValues.contacts]);
    var syncedContacts = useMemo(function () { var _a; return ((_a = initialValues.contacts) !== null && _a !== void 0 ? _a : []).filter(function (contact) { return contact.source; }); }, [initialValues.contacts]);
    var contactForm = useForm({
        resolver: zodResolver(publisherContactsSchema),
        defaultValues: {
            contacts: localContacts.map(function (contact) {
                var _a, _b, _c;
                return ({
                    id: (_a = contact.id) !== null && _a !== void 0 ? _a : null,
                    type: contact.type,
                    value: contact.value,
                    info: (_b = contact.info) !== null && _b !== void 0 ? _b : "",
                    isPrimary: (_c = contact.isPrimary) !== null && _c !== void 0 ? _c : false,
                });
            }),
            syncedContacts: syncedContacts.map(function (contact) {
                var _a, _b, _c, _d, _e;
                return ({
                    id: (_a = contact.id) !== null && _a !== void 0 ? _a : null,
                    type: contact.type,
                    value: (_b = contact.value) !== null && _b !== void 0 ? _b : "",
                    info: (_c = contact.info) !== null && _c !== void 0 ? _c : null,
                    isPrimary: (_d = contact.isPrimary) !== null && _d !== void 0 ? _d : false,
                    source: contact.source,
                    sourceId: (_e = contact.sourceId) !== null && _e !== void 0 ? _e : null,
                });
            }),
        },
        mode: "onSubmit",
        shouldFocusError: false,
    });
    var reset = contactForm.reset, control = contactForm.control;
    var _c = useFieldArray({
        control: control,
        name: "contacts",
    }), fields = _c.fields, append = _c.append, remove = _c.remove;
    useEffect(function () {
        reset({
            contacts: localContacts.map(function (contact) {
                var _a, _b, _c;
                return ({
                    id: (_a = contact.id) !== null && _a !== void 0 ? _a : null,
                    type: contact.type,
                    value: contact.value,
                    info: (_b = contact.info) !== null && _b !== void 0 ? _b : "",
                    isPrimary: (_c = contact.isPrimary) !== null && _c !== void 0 ? _c : false,
                });
            }),
            syncedContacts: syncedContacts.map(function (contact) {
                var _a, _b, _c, _d, _e;
                return ({
                    id: (_a = contact.id) !== null && _a !== void 0 ? _a : null,
                    type: contact.type,
                    value: (_b = contact.value) !== null && _b !== void 0 ? _b : "",
                    info: (_c = contact.info) !== null && _c !== void 0 ? _c : null,
                    isPrimary: (_d = contact.isPrimary) !== null && _d !== void 0 ? _d : false,
                    source: contact.source,
                    sourceId: (_e = contact.sourceId) !== null && _e !== void 0 ? _e : null,
                });
            }),
        });
    }, [initialValues, localContacts, syncedContacts, reset]);
    var handleAddContact = function () {
        append({
            id: null,
            type: PublisherContactTypeEnum.EMAIL,
            value: "",
            info: "",
            isPrimary: false,
        });
    };
    var prepareSubmitData = function (data) {
        var localUpdatedContacts = data.contacts
            .filter(function (contact) { return contact.value.trim() !== ""; })
            .map(function (contact) {
            var _a;
            return (__assign(__assign({}, (contact.id && { id: contact.id })), { type: contact.type, value: contact.value, info: contact.info || undefined, isPrimary: (_a = contact.isPrimary) !== null && _a !== void 0 ? _a : false }));
        });
        var syncedUpdatedContacts = data.syncedContacts.map(function (contact) {
            var _a, _b;
            return (__assign(__assign({}, (contact.id && { id: contact.id })), { type: contact.type, value: (_a = contact.value) !== null && _a !== void 0 ? _a : "", info: contact.info || undefined, isPrimary: (_b = contact.isPrimary) !== null && _b !== void 0 ? _b : false }));
        });
        return __assign(__assign({}, initialValues), { contacts: __spreadArray(__spreadArray([], syncedUpdatedContacts, true), localUpdatedContacts, true) });
    };
    return {
        localContacts: localContacts,
        syncedContacts: syncedContacts,
        contactForm: contactForm,
        fields: fields,
        regionName: regionName,
        removeContact: remove,
        handleAddContact: handleAddContact,
        prepareSubmitData: prepareSubmitData,
    };
};
